<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Notify, useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import type { QForm } from 'quasar';
import {
  mdiClose,
  mdiEye,
  mdiEyeOff,
  mdiFacebook,
  mdiGoogle,
} from '@quasar/extras/mdi-v7';
import BInput from 'src/shared/components/base/BInput/BInput.vue';
import BButton from 'src/shared/components/base/BButton/BButton.vue';
import useValidators from 'src/shared/composables/useValidators';
import { useAuth } from 'src/boot/auth';
import { useStrapiPost } from 'src/shared/composables/useStrapiApi';
import { stashPendingJoin } from 'src/modules/School/composables/useStudentJoin';
import { useAccountSignIn } from 'src/modules/User/composables/useAccountSignIn';
import { useProviderSignIn } from 'src/modules/User/composables/useProviderSignIn';

// Registration is STUDENT-ONLY: the backend assigns the student role server-side
// (teachers are created by admins). This page stays school-only in what it shows
// — it never links to the parent/child flow — but sign-in itself is the shared
// one, so a parent who ends up here is signed in and routed to their own app
// rather than refused.
interface SchoolAuthFormData {
  identifier: string;
  email: string;
  firstname: string;
  lastname: string;
  password: string;
  repassword: string;
  isRegister: boolean;
}

const route = useRoute();
const router = useRouter();
const $q = useQuasar();
const { t } = useI18n();
const { required, email } = useValidators();
const { signIn } = useAuth();
const { signInWithCredentials } = useAccountSignIn();
const { enabledProviders, isEnabled, providerSignIn } = useProviderSignIn();

// Invite links carry the class as ?classDocumentId=… (and the target semester as
// ?semesterDocumentId=…) on the register route.
const inviteClassDocumentId = computed(() =>
  typeof route.query.classDocumentId === 'string'
    ? route.query.classDocumentId
    : '',
);
const inviteSemesterDocumentId = computed(() =>
  typeof route.query.semesterDocumentId === 'string'
    ? route.query.semesterDocumentId
    : '',
);

// The Flash design puts white fields and a yellow button on purple, so the
// offset shadow/border/text that BInput and BButton derive from `shadowColor`
// all want the near-black ink colour rather than the SET App's white.
const FIELD_SHADOW = '#1c1c1c';

const formRef = ref<QForm | null>(null);
const visiblePassword = ref(false);
const visibleRePassword = ref(false);

const form = ref<SchoolAuthFormData>({
  identifier: '',
  email: '',
  firstname: '',
  lastname: '',
  password: '',
  repassword: '',
  isRegister: false,
});

const titleKey = computed(() =>
  form.value.isRegister
    ? 'SCHOOL_ACCOUNT_REGISTRATION'
    : 'SCHOOL_ACCOUNT_LOGIN',
);

onMounted(() => {
  form.value.isRegister = route.name === 'school.register';
});

async function submit() {
  const valid = formRef.value && (await formRef.value.validate());
  if (!valid) return;

  if (form.value.isRegister) {
    await registerSchoolUser();
    return;
  }

  // An invite link may have brought the user straight to the sign-in tab; stash it
  // so `signIn()` lands them on the join page instead of the dashboard.
  stashInviteIfPresent();
  await signInWithCredentials(form.value.identifier, form.value.password);
}

/** Carry an invite link's target (if any) into the post-sign-in redirect. */
function stashInviteIfPresent() {
  if (!inviteClassDocumentId.value) return;
  stashPendingJoin({
    classDocumentId: inviteClassDocumentId.value,
    ...(inviteSemesterDocumentId.value
      ? { semesterDocumentId: inviteSemesterDocumentId.value }
      : {}),
  });
}

async function registerSchoolUser() {
  // Student-only registration endpoint: role is assigned server-side and a jwt is
  // returned so the new student is logged in immediately.
  const { execute, error, data } = useStrapiPost(
    'users-permissions/users/student/register',
    {
      email: form.value.email,
      password: form.value.password,
      firstname: form.value.firstname,
      lastname: form.value.lastname,
    },
  );

  $q.loading.show();
  await execute().catch(() => null);
  $q.loading.hide();

  const jwt = (data.value as { jwt?: string } | null)?.jwt;
  if (error.value || !jwt) {
    Notify.create({ message: t('ACTION_FAILED'), color: 'negative' });
    return;
  }

  Notify.create({
    message: t('SCHOOL_REGISTER_SUCCESS'),
    color: 'positive',
  });

  // Registration invite link → stash the target so `signIn()` lands on the join
  // page, which enrolls into the shared class + semester and renders success or a
  // human-readable error. Without one it routes to the student's dashboard.
  stashInviteIfPresent();
  await signIn(jwt);
}

async function switchAuthMode() {
  // Preserve any invite params (classDocumentId/semesterDocumentId) across the
  // register ⇄ sign-in toggle so the enrollment target isn't lost.
  const query = { ...route.query };
  if (form.value.isRegister) {
    form.value.isRegister = false;
    await router.push({ name: 'school.login', query });
    return;
  }

  form.value.isRegister = true;
  await router.push({ name: 'school.register', query });
}
</script>

<template>
  <div class="school-auth-card-wrapper">
    <!-- No `scroll`/`full-vh` here: the q-page already is the scroll container
         (styleFn pins it to the viewport height with overflow-y auto), so a
         second one nests two scrollbars on the taller register form. -->
    <q-card
      class="school-auth-card full-width bg-transparent text-dark"
      flat
      square
      @keyup.enter="submit"
    >
      <q-card-section class="column items-center q-pb-none">
        <q-img
          src="/img/flashLogo_dark.svg"
          no-spinner
          alt="FlashStudies"
          class="flash-auth-logo"
        />
        <h3
          class="text-h4 q-my-md q-px-sm text-center text-uppercase text-weight-bold"
        >
          {{ $t(titleKey) }}
        </h3>
      </q-card-section>

      <q-card-section class="login-register-card">
        <p
          v-if="form.isRegister"
          class="text-dark text-center q-mb-none q-px-sm"
        >
          {{ $t('STUDENT_ACCOUNT') }}
        </p>

        <q-form
          ref="formRef"
          autocomplete="off"
          class="q-px-sm q-pt-md q-gutter-y-lg"
          @submit.prevent="submit"
        >
          <BInput
            v-if="!form.isRegister"
            v-model="form.identifier"
            :clear-icon="mdiClose"
            clearable
            no-error-icon
            lazy-rules
            :rules="[
              required,
              (val: string) => val.length < 50 || $t('MAXIMUM_50_CHARACTERS'),
            ]"
            type="text"
            :label="$t('USERNAME_OR_EMAIL')"
            autocomplete="username"
            :shadow-color="FIELD_SHADOW"
            label-color="dark"
            class="bg-white"
          />

          <BInput
            v-if="form.isRegister"
            v-model="form.email"
            clearable
            :clear-icon="mdiClose"
            no-error-icon
            lazy-rules
            type="email"
            :rules="[required, email]"
            :label="$t('EMAIL')"
            autocomplete="email"
            :shadow-color="FIELD_SHADOW"
            label-color="dark"
            class="bg-white"
          />

          <BInput
            v-model="form.password"
            clearable
            :clear-icon="mdiClose"
            no-error-icon
            :type="visiblePassword ? 'text' : 'password'"
            lazy-rules
            :rules="[
              required,
              (val: string) => val.length < 50 || $t('MAXIMUM_50_CHARACTERS'),
            ]"
            :label="$t('PASSWORD')"
            :autocomplete="!form.isRegister ? 'password' : 'new-password'"
            :shadow-color="FIELD_SHADOW"
            label-color="dark"
            class="bg-white"
          >
            <template #append>
              <q-icon
                :name="visiblePassword ? mdiEyeOff : mdiEye"
                class="cursor-pointer"
                @click="visiblePassword = !visiblePassword"
              />
            </template>
          </BInput>

          <BInput
            v-if="form.isRegister"
            v-model="form.repassword"
            clearable
            :clear-icon="mdiClose"
            no-error-icon
            :type="visibleRePassword ? 'text' : 'password'"
            lazy-rules
            :rules="[required, (val: string) => val === form.password]"
            :label="$t('RETYPE_PASSWORD')"
            autocomplete="new-password"
            :shadow-color="FIELD_SHADOW"
            label-color="dark"
            class="bg-white"
          >
            <template #append>
              <q-icon
                :name="visibleRePassword ? mdiEyeOff : mdiEye"
                class="cursor-pointer"
                @click="visibleRePassword = !visibleRePassword"
              />
            </template>
          </BInput>

          <BInput
            v-if="form.isRegister"
            v-model="form.firstname"
            clearable
            :clear-icon="mdiClose"
            no-error-icon
            lazy-rules
            type="text"
            :rules="[
              required,
              (val: string) => val.length < 50 || $t('MAXIMUM_50_CHARACTERS'),
            ]"
            :label="$t('FIRST_NAME')"
            autocomplete="given-name"
            :shadow-color="FIELD_SHADOW"
            label-color="dark"
            class="bg-white"
          />

          <BInput
            v-if="form.isRegister"
            v-model="form.lastname"
            clearable
            :clear-icon="mdiClose"
            no-error-icon
            lazy-rules
            type="text"
            :rules="[
              required,
              (val: string) => val.length < 50 || $t('MAXIMUM_50_CHARACTERS'),
            ]"
            :label="$t('LAST_NAME')"
            autocomplete="family-name"
            :shadow-color="FIELD_SHADOW"
            label-color="dark"
            class="bg-white"
          />
        </q-form>
      </q-card-section>

      <q-card-actions class="q-px-lg login-register-card">
        <BButton
          type="submit"
          unelevated
          size="md"
          color="secondary"
          text-color="dark"
          :shadow-color="FIELD_SHADOW"
          class="full-width"
          :label="$t(form.isRegister ? 'REGISTER' : 'SIGN_IN')"
          @click.prevent="submit"
        />
      </q-card-actions>

      <q-card-section class="text-center q-pt-none">
        <p
          v-if="!form.isRegister"
          class="q-mb-sm"
        >
          <q-btn
            flat
            dense
            size="sm"
            color="dark"
            :label="$t('FORGOT_PASSWORD')"
            :to="{ name: 'school.password.forgot' }"
          />
        </p>
        <p class="text-dark row justify-center items-center q-mb-none">
          {{ $t(form.isRegister ? 'HAVE_AN_ACCOUNT' : 'NEW_TO_OUR_SYSTEM') }}
          <q-btn
            flat
            size="sm"
            color="dark"
            :label="$t(form.isRegister ? 'SIGN_IN' : 'REGISTER')"
            @click="switchAuthMode"
          />
        </p>
      </q-card-section>

      <!-- Constrained to the form column: a full-bleed rule runs straight through
           the decorative star in the bottom-left corner. -->
      <q-separator
        v-if="enabledProviders.length"
        color="dark"
        class="login-register-card"
      />

      <!-- Only providers enabled in Strapi. School accounts are provisioned, so
           the backend logs in an existing account by email and never creates
           one from here. -->
      <q-card-section
        v-if="enabledProviders.length"
        class="row items-center justify-center q-gutter-md"
      >
        <q-btn
          v-if="isEnabled('facebook')"
          size="lg"
          round
          color="secondary"
          text-color="dark"
          :icon="mdiFacebook"
          :aria-label="$t('SIGN_IN')"
          @click="providerSignIn('facebook')"
        />
        <q-btn
          v-if="isEnabled('google')"
          size="lg"
          round
          color="secondary"
          text-color="dark"
          :icon="mdiGoogle"
          :aria-label="$t('SIGN_IN')"
          @click="providerSignIn('google')"
        />
      </q-card-section>
    </q-card>
  </div>
</template>

<style scoped>
.school-auth-card-wrapper {
  margin: 0 auto;
}

.school-auth-card {
  width: 100%;
  /* Stack above the fixed decoration art. */
  position: relative;
  z-index: 1;
}

.login-register-card {
  max-width: 400px;
  margin: auto;
}

.flash-auth-logo {
  width: 11rem;
  max-width: 60vw;
}

.full-vh {
  height: 100vh;
}
</style>
